import {
  ContentSharePartsFragment,
  PlatformClientContentPartsFragment,
  PlatformClientFolderPartsFragment
} from '@cma/generated/graphql'
import { FolderIcon } from '@cma/icons/FolderIcon'
import { FolderSharedIcon } from '@cma/icons/FolderSharedIcon'
import { PdfIcon } from '@cma/icons/PdfIcon'
import { SUPPORTED_IMAGE_FORMATS } from '@cma/common'
import { useEffect } from 'react'
import { FileSortEnum } from './useFileQuery'

const IMAGE_EXTENSIONS = ['png', 'gif', 'jpg', 'jpeg']

function getUrlExtension(url?: string | null) {
  if (!url) return ''

  const withoutQuery = url.split('?')[0].split('#')[0]
  const lastSegment = withoutQuery.split('/').pop() || ''
  const match = lastSegment.match(/\.([^.]+)$/)
  return match?.[1]?.toLowerCase() || ''
}

type PreviewableContent = Pick<
  PlatformClientContentPartsFragment,
  'platformClientContentContentType' | 'platformClientContentUrl'
>

export function isPdfContent(content: PreviewableContent) {
  if (content.platformClientContentContentType === 'application/pdf') {
    return true
  }
  return getUrlExtension(content.platformClientContentUrl) === 'pdf'
}

export function isImageContent(content: PreviewableContent) {
  if (
    content.platformClientContentContentType &&
    SUPPORTED_IMAGE_FORMATS.includes(content.platformClientContentContentType)
  ) {
    return true
  }
  return IMAGE_EXTENSIONS.includes(
    getUrlExtension(content.platformClientContentUrl)
  )
}

export function getFileIcon(
  type:
    | PlatformClientContentPartsFragment['__typename']
    | PlatformClientFolderPartsFragment['__typename'],
  isShared?: boolean
) {
  switch (type) {
    case 'Folder':
      return isShared ? FolderSharedIcon : FolderIcon
    default:
      return PdfIcon
  }
}

export function useRemoveMenuOverflow() {
  useEffect(() => {
    const removeOverflowHidden = () => {
      const menuElements = document.querySelectorAll(
        'div[id^="headlessui-menu-items-"]'
      )
      menuElements.forEach((element) => {
        if (element.classList.contains('overflow-hidden')) {
          element.classList.remove('overflow-hidden')
        }
      })
    }

    removeOverflowHidden()

    const observer = new MutationObserver(removeOverflowHidden)
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class']
    })

    return () => observer.disconnect()
  }, [])
}

export function getShareableContentName(item: ContentSharePartsFragment) {
  if (!item.shareable) {
    return ''
  }

  switch (item.shareable?.__typename) {
    case 'Brokerage':
    case 'Office':
    case 'Team':
      return item.shareable.name
    case 'PlatformClient':
      return item.shareable.platformAccountName
    default:
      return ''
  }
}

export function filterPlatformContent(
  items: (
    | PlatformClientContentPartsFragment
    | PlatformClientFolderPartsFragment
  )[],
  sortBy: FileSortEnum
) {
  switch (sortBy) {
    case FileSortEnum.Folders:
      return [...items].filter((c) => c.__typename === 'Folder')
    case FileSortEnum.Recent:
      return [...items].sort((a, b) => {
        const dateA = new Date(a.createdAt).getTime()
        const dateB = new Date(b.createdAt).getTime()
        return dateB - dateA
      })
    case FileSortEnum.Shared:
      return [...items].filter((c) => c?.contentShares?.length)
    case FileSortEnum.All:
    default:
      return items
  }
}

export function getFileNameFromUrl(file: {
  displayName?: string | null
  url?: string | null
}) {
  const url = file.url ?? ''
  const filename = file.displayName?.trim() || 'Untitled'
  let ext = ''

  if (!url) return filename

  try {
    const pathname = new URL(url).pathname
    const lastSegment = pathname.split('/').pop() || ''
    const match = lastSegment.match(/\.([^.?#]+)$/)
    if (match && match[1]) {
      ext = match[1]
    }
  } catch {
    const urlWithoutQuery = url.split('?')[0]
    const lastSegment = urlWithoutQuery.split('/').pop() || ''
    const match = lastSegment.match(/\.([^.?#]+)$/)
    if (match && match[1]) {
      ext = match[1]
    }
  }

  if (ext) {
    return `${filename}.${ext}`
  }

  return filename
}
